import { Text, ScreenContainer, View } from "@src/components";
import { sizes } from "@src/constants/sizes";
import { Alert } from "react-native";
import { router } from "expo-router";
import { NumericKeyboard } from "@src/components/Pin/NumericKeyboard";
import { PinDots } from "@src/components/Pin/PinDots";
import { StyleSheet } from "react-native-unistyles";
import { useState, useCallback, useEffect } from "react";
import { track } from "@visioni/a3s-analytics";
import { PinChangeFailed, PinChangeStarted } from "@src/features/auth/analytics";
import { FocusedSystemBars } from "@src/components/FocusedSystemBars";
import { BackgroundGradient } from "@src/components/BackgroundGradient";
import { BackRoundedButton } from "@src/components/BackRoundedButton";
import { ContactUsButton } from "@src/components/ContactUsButton";
import { secureStorage, SecureStorageKeys } from "@src/services/secure-storage";

const ChangePinScreen = () => {
  const [pin, setPin] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [failedAttempts, setFailedAttempts] = useState(0);

  useEffect(() => {
    track(PinChangeStarted);
  }, []);

  const handleChangePin = useCallback(
    async (value: number) => {
      const newPin = [...pin, value];

      if (newPin.length === 4) {
        // Verify the current PIN
        const storedPin = await secureStorage.get(SecureStorageKeys.userPin);

        if (newPin.join("") === storedPin) {
          // PIN verified - delete old PIN and navigate to setup new one
          await secureStorage.remove(SecureStorageKeys.userPin);
          await secureStorage.remove(SecureStorageKeys.legacyUserPin);
          setPin([]);
          setError(null);
          router.replace({ pathname: "/auth/pin", params: { flow: "change" } });
        } else {
          // Wrong PIN
          const newFailedAttempts = failedAttempts + 1;
          setFailedAttempts(newFailedAttempts);
          setPin([]);

          if (newFailedAttempts >= 3) {
            track(PinChangeFailed, {
              step: "current_pin",
              reason: "max_attempts_reached",
              attempts: newFailedAttempts,
            });
            Alert.alert("Too Many Attempts", "Please try again later.", [{ text: "OK", onPress: () => router.back() }]);
            return;
          }

          track(PinChangeFailed, { step: "current_pin", reason: "wrong_pin", attempts: newFailedAttempts });
          setError(`Wrong passcode. ${3 - newFailedAttempts} attempts remaining.`);
          return;
        }
      }

      setPin(newPin);
      setError(null);
    },
    [pin, failedAttempts],
  );

  const handleDelete = useCallback(() => {
    if (pin.length > 0) {
      setPin(pin.slice(0, -1));
    }
  }, [pin]);

  return (
    <>
      <FocusedSystemBars style="light" />
      <BackgroundGradient>
        <ScreenContainer
          edges={["top", "bottom"]}
          style={{ backgroundColor: "transparent" }}
          contentMaxWidth={sizes.maxWidthSm}
          scrollable
        >
          <View style={styles.backButtonContainer}>
            <BackRoundedButton onPress={() => router.back()} />
          </View>
          <View mt="9xl" alignItems="center">
            <Text textStyle="h3/regular" textAlign="center" style={styles.text}>
              Enter Current Passcode
            </Text>
          </View>
          <View>
            <View mt="4xl" alignItems="center">
              <PinDots pinLength={pin.length} />
            </View>
            {error && (
              <View style={styles.errorContainer}>
                <Text textStyle="sm/regular" textAlign="center" style={styles.error}>
                  {error}
                </Text>
              </View>
            )}
          </View>
          <View flex={1} alignItems="center">
            <NumericKeyboard onPress={handleChangePin} onDelete={handleDelete} />
          </View>
          <View
            my="4xl"
            px="4xl"
            flexDirection="row"
            flexWrap="wrap"
            justifyContent="center"
            alignItems="center"
            gap="sm"
          >
            <Text textStyle="sm/regular" textAlign="center" style={styles.text}>
              Having a problem?
            </Text>
            <ContactUsButton />
          </View>
        </ScreenContainer>
      </BackgroundGradient>
    </>
  );
};

const styles = StyleSheet.create((theme) => ({
  backButtonContainer: {
    marginLeft: theme.spacing["4xl"],
    marginTop: theme.spacing["3xl"],
  },
  error: {
    color: theme.colors.status.error,
  },
  errorContainer: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: -theme.spacing["8xl"],
    justifyContent: "center",
    alignItems: "center",
  },
  text: {
    color: theme.colors.interactive.buttons.default_foreground,
  },
}));

export default ChangePinScreen;
